import { useTranslation } from 'react-i18next'
import { BiCheck } from 'react-icons/bi'
import {
  Badge,
  Flex,
  List,
  ListIcon,
  ListItem,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Stack,
  Text,
  Wrap,
} from '@chakra-ui/react'

import { useIsMobile } from '~hooks/useIsMobile'
import Button from '~components/Button'
import InlineMessage from '~components/InlineMessage'
import { ModalCloseButton } from '~components/Modal'

const InlineTextListItem = ({
  children,
}: {
  children: string
}): JSX.Element => (
  <ListItem display="flex">
    <Flex h="1.5rem" align="center">
      <ListIcon as={BiCheck} />
    </Flex>
    {children}
  </ListItem>
)

interface ConfirmationScreenProps {
  onCancel: () => void
  onDownload: () => void
  isDownloading: boolean
  responsesCount: number
}

export const ConfirmationScreen = ({
  onCancel,
  isDownloading,
  onDownload,
  responsesCount,
}: ConfirmationScreenProps): JSX.Element => {
  const isMobile = useIsMobile()
  const i18n = useTranslation()
  return (
    <>
      <ModalCloseButton />
      <ModalHeader color="secondary.700" pr="4.5rem">
        <Wrap shouldWrapChildren direction="row" align="center">
          <Text>{i18n.t<string>('responsePage.downloadResponse')}</Text>
          <Badge w="fit-content" colorScheme="success">
            beta
          </Badge>
        </Wrap>
      </ModalHeader>
      <ModalBody whiteSpace="pre-wrap" color="secondary.500">
        <Stack spacing="1rem">
          <Text>
            {i18n.t<string>('responsePage.separateZip')}{' '}
            <b>{i18n.t<string>('responsePage.eachResponse')}</b>{' '}
            {i18n.t<string>('responsePage.adjust')}
            <br />
            <br />
            <b>{i18n.t<string>('responsePage.numberOfResponse')}</b>{' '}
            {responsesCount.toLocaleString()}
            <br />
            <b>{i18n.t<string>('responsePage.estimate')}:</b>{' '}
            {i18n.t<string>('responsePage.perResponse')}
          </Text>
          <InlineMessage>
            <Stack>
              <Text textStyle="subhead-1">
                {i18n.t<string>('responsePage.intensive')}
              </Text>
              <List>
                <InlineTextListItem>
                  {i18n.t<string>('responsePage.IE')}
                </InlineTextListItem>
                <InlineTextListItem>
                  {i18n.t<string>('responsePage.networkConnect')}
                </InlineTextListItem>
                <InlineTextListItem>
                  {i18n.t<string>('responsePage.sufficient')}
                </InlineTextListItem>
              </List>
            </Stack>
          </InlineMessage>
          {responsesCount === 0 && (
            <InlineMessage variant="warning">
              {i18n.t<string>('responsePage.dateRange')}
            </InlineMessage>
          )}
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Stack
          direction={{ base: 'column', md: 'row-reverse' }}
          w="100%"
          justify="end"
        >
          <Button
            isFullWidth={isMobile}
            onClick={onDownload}
            isLoading={isDownloading}
            isDisabled={responsesCount === 0}
          >
            {i18n.t<string>('responsePage.startDownload')}
          </Button>
          <Button
            isFullWidth={isMobile}
            variant="clear"
            colorScheme="secondary"
            onClick={onCancel}
            isDisabled={isDownloading}
          >
            {i18n.t<string>('responsePage.cancel')}
          </Button>
        </Stack>
      </ModalFooter>
    </>
  )
}
